'use client';

import { Suspense } from 'react';

import Footer from '@/components/Footer';
import Header from '@/components/Header';
import '@/styles/headerfooter.css';
import '@/styles/responsive/legal.css';
import ZapChat from '@/components/ZapChat';

export default function TermsPage() {
  return (
    <>
      <ZapChat />
      <main id="main-content" className="min-h-screen">
        <Suspense
          fallback={
            <div className="fixed top-0 right-0 left-0 z-50 h-[72px] border-black/5 border-b bg-white shadow-sm" />
          }
        >
          <Header />
        </Suspense>

        {/* Terms of Service Section */}
        <section className="terms-page">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Terms of Service</h2>
            </div>

            <div className="terms-content">
              <section className="terms-section">
                <h3 className="text-gray-950">1. Introduction</h3>
                <p>
                  Welcome to ZappingAI. These Terms of Service (&quot;Terms&quot;) govern your use
                  of our website, products, and services. By accessing or using our services, you
                  agree to be bound by these Terms. If you do not agree, you may not use our
                  services.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">2. User Obligations</h3>
                <p>
                  You agree to use our services only for lawful purposes and in a manner that does
                  not infringe on the rights of others or restrict their use and enjoyment of the
                  services. You are prohibited from engaging in any activity that could damage,
                  disable, or impair our services.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">3. Intellectual Property</h3>
                <p>
                  Upon full and final payment of all contract fees, ZappingAI assigns to the Client
                  100% ownership of the Intellectual Property (IP) for all custom business workflows,
                  unique automation logic, and bespoke scripts developed specifically for the Client
                  (the &apos;Deliverables&apos;). While ZappingAI retains ownership of its core underlying
                  platform and pre-existing code modules, the Client is granted a perpetual,
                  royalty-free licence to use these elements as part of their integrated system. The
                  Client has the right to demand a full export of their proprietary logic and
                  configuration data as soon as all project payments are completed.
                </p>
                <p>
                  All content on this website, including text, graphics, logos, and images, is the
                  property of ZappingAI or its content suppliers and is protected by intellectual
                  property laws. You may not reproduce, distribute, or create derivative works from
                  our content without our explicit permission.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">4. AI Usage and Content Generation</h3>
                <p>
                  Our AI services generate content based on your inputs. You retain ownership of
                  content you create, but grant us a licence to use and improve our AI models based
                  on aggregated usage patterns. Generated content must comply with applicable laws
                  and our usage policies.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">5. Data Processing and Privacy</h3>
                <p>
                  We process your data in accordance with our Privacy Policy and applicable UK data
                  protection laws including the UK GDPR. By using our services, you consent to the
                  collection, storage, and processing of your data as described in our privacy
                  policy. Your data is used to provide and improve our AI services.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">6. Service Availability</h3>
                <p>
                  Our services are provided on an &quot;as-is&quot; and &quot;as-available&quot;
                  basis. We do not guarantee uninterrupted or error-free service. We may suspend or
                  modify services for maintenance, upgrades, or other operational reasons. Nothing
                  in these Terms affects your statutory rights as a consumer.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">7. Limitation of Liability</h3>
                <p>
                  ZappingAI will not be liable for any damages arising from the use or inability to
                  use our services. Our liability is limited to the fullest extent permitted by UK
                  law. This includes direct, indirect, incidental, consequential, or special
                  damages. Nothing in this section limits our liability for death or personal injury
                  resulting from our negligence, or for fraud.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">8. Prohibited Activities</h3>
                <p>
                  You may not use our services for any unlawful purpose, including but not limited
                  to: illegal activities, harassment, spam, malware distribution, copyright
                  infringement, or any activity that violates our acceptable use policy.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">9. API Usage</h3>
                <p>
                  If you use our API, you agree to comply with our API documentation and usage
                  guidelines. Excessive or abusive API usage may result in suspension or termination
                  of your access. API usage is subject to rate limits and terms specific to your
                  plan.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">10. Subscription Terms</h3>
                <p>
                  By agreeing to these Terms, you also agree to our{' '}
                  <a href="/subscription">Subscription Terms</a>, which govern our subscription
                  services and billing arrangements.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">11. Refund Policy</h3>
                <p>
                  By agreeing to these Terms, you also agree to our{' '}
                  <a href="/refund">Refund Policy</a>, which outlines our policy for refunds and
                  cancellations.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">12. Privacy Policy</h3>
                <p>
                  By agreeing to these Terms, you also agree to our{' '}
                  <a href="/privacy">Privacy Policy</a>, which governs how we collect, use, and
                  protect your personal information in accordance with UK data protection laws.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">13. Mobile Policy</h3>
                <p>
                  The ZappingAI mobile message service (the &quot;Service&quot;) is operated by
                  ZappingAI (&quot;ZappingAI&quot;, &quot;we&quot;, or &quot;us&quot;). Your use of
                  the Service constitutes your agreement to these terms and conditions (&quot;Mobile
                  Terms&quot;). We may modify or cancel the Service or any of its features without
                  notice. To the extent permitted by applicable law, we may also modify these Mobile
                  Terms at any time and your continued use of the Service shall constitute your
                  acceptance of such changes.
                </p>
                <p>
                  By consenting to ZappingAI&apos;s SMS/text messaging service, you agree to receive
                  recurring SMS/text messages from and on behalf of ZappingAI through your wireless
                  provider to the mobile number you provided, even if your mobile number is
                  registered on any state or federal Do Not Call list. Text messages may be sent
                  using an automatic telephone dialing system or other technology. Service-related
                  messages may include updates, alerts, and information (e.g., order updates,
                  account alerts, etc.). Promotional messages may include promotions, specials, and
                  other marketing offers (e.g., cart reminders).
                </p>
                <p>
                  You understand that you do not have to sign up for this program in order to make
                  any purchases, and your consent is not a condition of any purchase with ZappingAI.
                  Your participation in this program is completely voluntary.
                </p>
                <p>
                  We do not charge for the Service, but you are responsible for all charges and fees
                  associated with text messaging imposed by your wireless provider. Message
                  frequency varies. Message and data rates may apply. Check your mobile plan and
                  contact your wireless provider for details. You are solely responsible for all
                  charges related to SMS/text messages, including charges from your wireless
                  provider.
                </p>
                <p>
                  You may opt-out of the Service at any time. Text the single keyword command STOP
                  to ZappingAI or click the unsubscribe link (where available) in any text message
                  to cancel. You&apos;ll receive a one-time opt-out confirmation text message. No
                  further messages will be sent to your mobile device, unless initiated by you. If
                  you have subscribed to other ZappingAI mobile message programs and wish to cancel,
                  except where applicable law requires otherwise, you will need to opt out
                  separately from those programs by following the instructions provided in their
                  respective mobile terms.
                </p>
                <p>
                  For Service support or assistance, text HELP to ZappingAI or{' '}
                  <a href="/#contact">contact us</a>.
                </p>
                <p>
                  We may change any short code or telephone number we use to operate the Service at
                  any time and will notify you of these changes. You acknowledge that any messages,
                  including any STOP or HELP requests, you send to a short code or telephone number
                  we have changed may not be received and we will not be responsible for honouring
                  requests made in such messages.
                </p>
                <p>
                  The wireless carriers supported by the Service are not liable for delayed or
                  undelivered messages. You agree to provide us with a valid mobile number. If you
                  get a new mobile number, you will need to sign up for the program with your new
                  number.
                </p>
                <p>
                  To the extent permitted by applicable law, you agree that we will not be liable
                  for failed, delayed, or misdirected delivery of any information sent through the
                  Service, any errors in such information, and/or any action you may or may not take
                  in reliance on the information or Service.
                </p>
                <p>
                  We respect your right to privacy. To see how we collect and use your personal
                  information, please see our <a href="/privacy">Privacy Notice</a>.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">14. Changes to Terms</h3>
                <p>
                  We reserve the right to modify these Terms at any time. We will notify you of any
                  changes by posting the new Terms on this page. Your continued use of our services
                  after such modifications constitutes your acceptance of the new Terms. For
                  consumers, changes will be notified in advance and you will have the right to
                  terminate the agreement if you do not agree to the changes.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">15. Termination</h3>
                <p>
                  We may terminate or suspend your access to our services immediately, without prior
                  notice or liability, for any reason or no reason, including but not limited to
                  breach of these Terms. This does not affect your statutory rights under UK law.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">16. Governing Law</h3>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of
                  England and Wales, without regard to its conflict of law principles.
                </p>
              </section>

              <section className="terms-section">
                <h3 className="text-gray-950">17. Contact Information</h3>
                <p>
                  If you have any questions about these Terms, please{' '}
                  <a href="/#contact">contact us</a>.
                </p>
              </section>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}
